// src/configs/InitializeApp.js
import { theme as antdTheme } from 'antd';
import {
  BODY_BACKGROUND,
  BORDER,
  DARK_MODE,
  GRAY_SCALE,
  THEME_COLOR,
} from 'constants/ThemeConstant';

// APP-SPECIFIC CONFIG FILES
import appmulti from './apps/appmulti/appmulti.js';
import applekkus from './apps/applekkus/applekkus.js';
import appfunnel from './apps/appfunnel/appfunnel.js';

import { onProduction } from 'utils/helpers';

// scaffold

// appmulti
import appmultiOperators from '../modules/app-views/_apps/appmulti/operators/config';
import plans from '../modules/app-views/_apps/appmulti/plans/config';
import appmultiQuotations from '../modules/app-views/_apps/appmulti/quotations/config';
import receivables from '../modules/app-views/_apps/appmulti/receivables/config';
import orders from '../modules/app-views/_apps/appmulti/orders/config';
import appmultiLeads from '../modules/app-views/_apps/appmulti/leads/config';
import coupons from '../modules/app-views/_apps/appmulti/coupons/config';

// crm
import crm from '../modules/app-views/crm/config';

// applekkus
import companies from '../modules/app-views/companies/config';
import customers from '../modules/app-views/customers/config';
import suppliers from '../modules/app-views/suppliers/config';
import collaborators from '../modules/app-views/collaborators/config';
import products from '../modules/app-views/products/config';
import invoices from '../modules/app-views/invoices/config';
import fiscalConfig from 'modules/app-views/fiscalConfig/config';
import dataSearch from '../modules/app-views/dataSearch/config';
import services from '../modules/app-views/services/config';
import toolbox from '../modules/app-views/toolbox/config';
import serviceOrders from '../modules/app-views/serviceOrders/config';
import landingPages from '../modules/app-views/landingPages/config';
import omnichannel from '../modules/app-views/omnichannel/config';
import offers from '../modules/app-views/offers/config';
import leads from '../modules/app-views/leads/config';
import goodsReceipt from '../modules/app-views/goodsReceipt/config';
import ticketEvents from 'modules/app-views/ticketEvents/config';

// appcaup
import caupDashboard from '../modules/app-views/_apps/appcaup/dashboard/config';

// appsindico
import units from '../modules/app-views/_apps/appsindico/units/config';
import unitProfiles from '../modules/app-views/_apps/appsindico/unitProfiles/config';
import parkingSpots from '../modules/app-views/_apps/appsindico/parkingSpots/config';

// IMPORTA metadados neutros (sem efeitos colaterais) para evitar ciclos
import { APPLICATIONS, FORCE_APP } from './initializer';

// MODULES 2.0 (core)
import account from 'modules/app-views/account/config';
import welcome from '../modules/app-views/welcome/config';

// PROFILES is an isolated module (avoids circular imports)
import { PROFILES } from './profiles';
import { opacityToHex } from 'utils/colors';

/*
 * @summary Module registration checklist
 * 1) Add the new module import above.
 * 2) Add it into MODULES object below.
 * 3) Ensure it exists in the specific app config (app<<name>>.js) MODULES array.
 * 4) Add MAIN_ROUTE_* into routes/RoutesIndex.js (componentsByPath).
 */

// ================================================================================================================== //
// PRE-CONFIG FOR MODULES (runs with selected app in dev/prod)
// ================================================================================================================== //

/**
 * Some modules need to adapt based on the selected app ID (FORCE_APP or resolved).
 * We keep the original behavior: call `preConfigChanges(appId)` during initialization.
 */
account.preConfigChanges(FORCE_APP?.ID);
welcome.preConfigChanges(FORCE_APP?.ID);

// appmulti
appmultiOperators.preConfigChanges(FORCE_APP?.ID);
plans.preConfigChanges(FORCE_APP?.ID);
appmultiQuotations.preConfigChanges(FORCE_APP?.ID);
receivables.preConfigChanges(FORCE_APP?.ID);
orders.preConfigChanges(FORCE_APP?.ID);
appmultiLeads.preConfigChanges(FORCE_APP?.ID);
coupons.preConfigChanges(FORCE_APP?.ID);

// crm
crm.preConfigChanges(FORCE_APP?.ID);

// applekkus
companies.preConfigChanges(FORCE_APP?.ID);
customers.preConfigChanges(FORCE_APP?.ID);
suppliers.preConfigChanges(FORCE_APP?.ID);
collaborators.preConfigChanges(FORCE_APP?.ID);
products.preConfigChanges(FORCE_APP?.ID);
invoices.preConfigChanges(FORCE_APP?.ID);
fiscalConfig.preConfigChanges(FORCE_APP?.ID);
dataSearch.preConfigChanges(FORCE_APP?.ID);
services.preConfigChanges(FORCE_APP?.ID);
toolbox.preConfigChanges(FORCE_APP?.ID);
serviceOrders.preConfigChanges(FORCE_APP?.ID);
landingPages.preConfigChanges(FORCE_APP?.ID);
omnichannel.preConfigChanges(FORCE_APP?.ID);
offers.preConfigChanges(FORCE_APP?.ID);
leads.preConfigChanges(FORCE_APP?.ID);
goodsReceipt.preConfigChanges(FORCE_APP?.ID);
ticketEvents.preConfigChanges(FORCE_APP?.ID);

// appcaup
caupDashboard.preConfigChanges(FORCE_APP?.ID);

// appsindico
units.preConfigChanges(FORCE_APP?.ID);
unitProfiles.preConfigChanges(FORCE_APP?.ID);
parkingSpots.preConfigChanges(FORCE_APP?.ID);

// ================================================================================================================== //
// MODULE REGISTRY (exported)
// ================================================================================================================== //

export const MODULES = {
  // core
  account,
  welcome,

  // appmulti
  appmultiOperators,
  plans,
  appmultiQuotations,
  receivables,
  orders,
  appmultiLeads,
  coupons,

  // crm
  crm,

  // applekkus
  companies,
  customers,
  suppliers,
  collaborators,
  products,
  invoices,
  fiscalConfig,
  dataSearch,
  services,
  toolbox,
  serviceOrders,
  landingPages,
  omnichannel,
  offers,
  leads,
  goodsReceipt,
  ticketEvents,

  // appcaup
  caupDashboard,

  // appsindico
  units,
  unitProfiles,
  parkingSpots,
};

// ================================================================================================================== //
// THEME TOKENS (base + component overrides)
// ================================================================================================================== //

// Returns the primary color based on app/theme selection.
// On DEV uses FORCE_APP.THEME_COLOR if available; on PROD resolves by domain.
const getApplicationColorTheme = (opacity = 1) => {
  const url = new URL(window.location.href);
  const domain = url.hostname;

  let primaryColor = opacity < 1 ? `${THEME_COLOR.BLUE}${opacityToHex(opacity)}` : THEME_COLOR.BLUE;

  if (!onProduction()) {
    if (FORCE_APP?.THEME_COLOR && THEME_COLOR?.[FORCE_APP.THEME_COLOR]) {
      primaryColor =
        opacity < 1
          ? `${THEME_COLOR?.[FORCE_APP.THEME_COLOR]}${opacityToHex(opacity)}`
          : THEME_COLOR?.[FORCE_APP.THEME_COLOR];
    }
  } else {
    for (const app of APPLICATIONS) {
      if (Array.isArray(app?.DOMAINS) && app.DOMAINS.includes(domain)) {
        if (app?.THEME_COLOR && THEME_COLOR?.[app.THEME_COLOR]) {
          primaryColor =
            opacity < 1
              ? `${THEME_COLOR?.[app.THEME_COLOR]}${opacityToHex(opacity)}`
              : THEME_COLOR?.[app.THEME_COLOR];
        }
      }
    }
  }

  return primaryColor;
};

/**
 * Base theme tokens used by Ant Design theme config.
 */
export const baseTheme = {
  borderRadius: 8,
  colorPrimary: getApplicationColorTheme(),
  colorSuccess: THEME_COLOR.CYAN,
  colorWarning: THEME_COLOR.GOLD,
  colorError: THEME_COLOR.VOLCANO,
  colorInfo: THEME_COLOR.BLUE,
  colorText: GRAY_SCALE.GRAY,
  colorBorder: BORDER.BASE_COLOR,
  bodyBg: BODY_BACKGROUND,
  controlHeight: 40,
  controlHeightLG: 48,
  controlHeightSM: 36,
  fontFamily: `'Roboto', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial,
    'Noto Sans', sans-serif, 'Apple Color Emoji', 'Segoe UI Emoji', 'Segoe UI Symbol',
    'Noto Color Emoji'`,
  fontSizeHeading2: 22,
  fontSizeHeading4: 17,
  // colorLink: getApplicationColorTheme(),

  // Link tokens (also used in global styles)
  colorLink: getApplicationColorTheme(),
  colorLinkHover: getApplicationColorTheme(0.5),
  colorLinkActive: getApplicationColorTheme(0.8),
};

// Build Ant Design component-level token overrides.
const getBaseComponentThemeConfig = ({
  Button = {},
  Menu = {},
  Card = {},
  Table = {},
  Select = {},
  DatePicker = {},
  Layout = {},
  Input = {},
  Dropdown = {},
  Calendar = {},
} = {}) => {
  return {
    Button: { ...Button },
    Dropdown: {
      controlPaddingHorizontal: 20,
      controlHeight: 37,
      borderRadiusLG: 10,
      paddingXXS: '4px 0px',
      ...Dropdown,
    },
    Calendar: { ...Calendar },
    Checkbox: { lineWidth: 2, borderRadiusSM: 4 },
    Card: { colorTextHeading: GRAY_SCALE.GRAY_DARK, paddingLG: 20, ...Card },
    Layout: { bodyBg: BODY_BACKGROUND, headerBg: GRAY_SCALE.WHITE, ...Layout },
    Breadcrumb: {
      colorTextDescription: GRAY_SCALE.GRAY_LIGHT,
      colorText: baseTheme.colorPrimary,
      colorBgTextHover: 'transparent',
    },
    Menu: {
      itemBg: 'transparent',
      colorActiveBarHeight: 0,
      colorActiveBarWidth: 0,
      itemColor: GRAY_SCALE.GRAY,
      horizontalItemSelectedColor: baseTheme.colorPrimary,
      itemHoverColor: baseTheme.colorPrimary,
      itemSelectedColor: baseTheme.colorPrimary,
      itemSelectedBg: rgba(baseTheme.colorPrimary, 0.2),
      itemHoverBg: 'transparent',
      radiusItem: 0,
      subMenuItemBg: 'transparent',
      itemMarginInline: 4,
      controlHeightLG: 40,
      controlHeightSM: 22,
      ...Menu,
    },
    Input: { ...Input },
    Pagination: {
      paginationItemSize: 30,
      borderRadius: '50%',
      colorBgContainer: baseTheme.colorPrimary,
      colorPrimary: GRAY_SCALE.WHITE,
      colorPrimaryHover: GRAY_SCALE.WHITE,
    },
    Steps: { wireframe: true, controlHeight: 32, waitIconColor: GRAY_SCALE.GRAY_LIGHT },
    DatePicker: {
      controlHeight: 40,
      controlHeightSM: 26,
      borderRadiusSM: 6,
      lineWidthBold: 0,
      ...DatePicker,
    },
    Radio: { fontSizeLG: 18 },
    Slider: {
      colorPrimaryBorder: rgba(baseTheme.colorPrimary, 0.8),
      colorPrimaryBorderHover: baseTheme.colorPrimary,
    },
    Select: {
      paddingXXS: '4px 0px',
      controlHeight: 40,
      controlHeightSM: 30,
      controlItemBgActive: rgba(baseTheme.colorPrimary, 0.1),
      // Fix: undefined CSS variables for disabled Select
      colorBgContainerDisabled: 'rgba(0, 0, 0, 0.04)',
      ...Select,
    },
    TreeSelect: { controlHeightSM: 24 },
    Tooltip: { colorBgDefault: rgba(GRAY_SCALE.DARK, 0.75), controlHeight: 32 },
    Timeline: { lineType: 'dashed' },
    Tag: { lineHeight: 2.1 },
    Table: { colorText: GRAY_SCALE.GRAY, tableHeaderBg: 'transparent', ...Table },
  };
};

export const lightTheme = {
  token: { ...baseTheme, colorTextBase: GRAY_SCALE.GRAY },
  components: getBaseComponentThemeConfig({
    Select: { selectorBg: '#ffffff' },
  }),
};

export const darkTheme = {
  algorithm: antdTheme.darkAlgorithm,
  token: {
    ...baseTheme,
    colorTextBase: DARK_MODE.TEXT_COLOR,
    colorBgBase: DARK_MODE.BG_COLOR,
    colorBorder: DARK_MODE.BORDER_BASE_COLOR,
    colorText: DARK_MODE.TEXT_COLOR,
  },
  components: getBaseComponentThemeConfig({
    Button: {
      controlOutline: 'transparent',
      colorBorder: DARK_MODE.BORDER_BASE_COLOR,
      colorText: DARK_MODE.TEXT_COLOR,
    },
    Card: { colorTextHeading: DARK_MODE.HEADING_COLOR },
    Layout: { bodyBg: DARK_MODE.BODY_BG, headerBg: DARK_MODE.HEADER_BG },
    Menu: {
      itemSelectedBg: rgba(baseTheme.colorPrimary, 0.25),
      horizontalItemSelectedColor: GRAY_SCALE.WHITE,
      itemHoverColor: GRAY_SCALE.WHITE,
      itemSelectedColor: GRAY_SCALE.WHITE,
      itemColor: DARK_MODE.TEXT_COLOR,
      colorActiveBarWidth: 0,
      boxShadowSecondary: DARK_MODE.DROP_DOWN_SHADOW,
    },
    Dropdown: { boxShadowSecondary: DARK_MODE.DROP_DOWN_SHADOW },
    Calendar: { controlItemBgActive: '#303a4e' },
    Select: {
      controlOutline: 'transparent',
      controlItemBgActive: DARK_MODE.ACTIVE_BG_COLOR,
      controlItemBgHover: DARK_MODE.HOVER_BG_COLOR,
      boxShadowSecondary: DARK_MODE.DROP_DOWN_SHADOW,
      // Fix: undefined CSS variables for disabled Select
      colorBgContainerDisabled: 'rgba(255, 255, 255, 0.08)',
      // selectorBg: DARK_MODE.BG_COLOR, // --select-background
    },
    Input: { controlOutline: 'transparent' },
    DatePicker: {
      controlOutline: 'transparent',
      boxShadowSecondary: DARK_MODE.DROP_DOWN_SHADOW,
      controlItemBgActive: DARK_MODE.ACTIVE_BG_COLOR,
      controlItemBgHover: DARK_MODE.HOVER_BG_COLOR,
    },
    Table: { colorText: DARK_MODE.TEXT_COLOR, colorTextHeading: DARK_MODE.HEADING_COLOR },
  }),
};

// Convert hex to rgba string.
export function rgba(hex, opacity = 1) {
  if (!/^#([A-Fa-f0-9]{3}){1,2}$/.test(hex)) {
    throw new Error('Invalid Hex');
  }
  const decimal = parseInt(hex.substring(1), 16);
  const r = (decimal >> 16) & 255;
  const g = (decimal >> 8) & 255;
  const b = decimal & 255;
  return `rgba(${r}, ${g}, ${b}, ${opacity})`;
}

// -------------------------------------------------------------------------------------------------
// Reexport for backward compatibility (does not create cycles)
// - APPLICATIONS/FORCE_APP: come from appMeta
// - PROFILES: now comes from ./profiles (re-exported under the same name)
// -------------------------------------------------------------------------------------------------
export { APPLICATIONS, FORCE_APP, PROFILES };
